// Resolve a country code or display name to an ISO2 code and a local flag SVG
// (public/assets/flags/4x3/<iso2>.svg). No hardcoded country list: the
// name-to-code index is built once at runtime from Intl.DisplayNames, plus a
// few aliases for the rate platform's naming quirks.

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[_,.'()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Platform / colloquial names that differ from the Intl English display name.
const ALIASES = {
  'united states of america': 'US',
  'usa': 'US',
  'uk': 'GB',
  'great britain': 'GB',
  'england': 'GB',
  'uae': 'AE',
  'south korea': 'KR',
  'korea republic of': 'KR',
  'korea south': 'KR',
  'north korea': 'KP',
  'russian federation': 'RU',
  'viet nam': 'VN',
  'czech republic': 'CZ',
  'turkiye': 'TR',
  'ivory coast': 'CI',
  'cote d ivoire': 'CI',
  'burma': 'MM',
  'cape verde': 'CV',
  'swaziland': 'SZ',
  'east timor': 'TL',
  'macau': 'MO',
  'palestine': 'PS',
  'laos': 'LA',
  'brunei darussalam': 'BN',
  'syrian arab republic': 'SY',
  'tanzania united republic of': 'TZ',
  'iran islamic republic of': 'IR',
  'moldova republic of': 'MD',
  'bolivia plurinational state of': 'BO',
  'venezuela bolivarian republic of': 'VE',
  'congo democratic republic of the': 'CD',
  'congo the democratic republic of': 'CD',
  'scotland': 'GB',
  'wales': 'GB',
};

let NAME_INDEX = null;

const buildIndex = () => {
  const index = { ...Object.fromEntries(
    Object.entries(ALIASES).map(([k, v]) => [k, v])
  ) };
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (const a of A) {
      for (const b of A) {
        const code = a + b;
        let name;
        try {
          name = dn.of(code);
        } catch {
          continue;
        }
        // Unknown codes echo back the code itself. First code wins so
        // canonical codes beat reserved duplicates (GB before UK, both of
        // which display as "United Kingdom" - the flag file is gb.svg).
        const key = name && name !== code ? normalize(name) : null;
        if (key && !(key in index)) {
          index[key] = code;
        }
      }
    }
  } catch {
    // Intl.DisplayNames unavailable: aliases still work
  }
  return index;
};

// Returns an ISO2 code (e.g. 'GB') or null.
export const getFlagIso2 = (codeOrName) => {
  if (!codeOrName) return null;
  const raw = String(codeOrName).trim();
  // Already an ISO2 code (map reserved UK to canonical GB)
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const upper = raw.toUpperCase();
    return upper === 'UK' ? 'GB' : upper;
  }
  if (!NAME_INDEX) NAME_INDEX = buildIndex();
  return NAME_INDEX[normalize(raw)] || null;
};

// Local SVG path for an ISO2 code (files copied to public/assets/flags/4x3).
export const flagSrc = (iso2) =>
  iso2 ? `/assets/flags/4x3/${String(iso2).toLowerCase()}.svg` : null;
